import { isEqual, kebabCase, merge, pick, sortBy } from "lodash";
import { store } from "../store/application";
export const applicationMixin = {
  data() {
    return {
      confirmationModalVisible: false,
      active: 0,
      showApplication: false,
      application: store.application,
      errors: store.errors,
      showErrors: store.showErrors
    };
  },
  computed: {
    /**
     * competitionForm: checks whether it exisits and parses the JSON
     * @returns	{object}			The parsed json, if it exisits
     */
    competitionForm() {
      if (!this.state.competition.application_template) return [];
      return JSON.parse(this.state.competition.application_template);
    },
    /**
     * fieldsets: checks that the application form has fieldsets
     * @returns	{array}			Returns and array of fieldsets
     */
    fieldsets() {
      if (!this.competitionForm.fieldset) return [];
      return this.competitionForm.fieldset;
    },
    /**
     * tabs: used to create usable data for each of the tabs
     * @returns	{array}		contains each of the fieldset's name and hash
     */
    tabs() {
      if (!this.fieldsets) return [];
      return this.fieldsets.map(item =>
        Object.assign(
          {},
          {
            name: item.legend,
            hash: this.hashFriendly(item.legend)
          }
        )
      );
    },
    hasErrors() {
      return Object.keys(this.errors).length > 0;
    },
    groupedErrors() {
      const keys = Object.keys(this.errors).map(i => i.split("."));
      const fieldInput = keys.reduce((obj, item) => {
        let field = this.getFieldset(item[0]);
        if (field.legend in obj === false) {
          obj[field.legend] = [];
        }
        if (obj[field.legend].indexOf(field.question) === -1) {
          obj[field.legend].push(field.question);
        }
        return obj;
      }, {});
      return fieldInput;
    },
    reviewMode() {
      return store.reviewMode;
    }
  },
  watch: {
    active: function() {
      return this.saveApplication();
    }
  },
  created() {
    document.title = `${this.state.project.ref} Application Form :: ${
      document.title
    }`;

    const checkChanges = e => this.checkChanges(e);

    window._temp = this.state.project.application;
    window._project = this.state.project.uuid;

    window.addEventListener("turbolinks:before-visit", checkChanges);
    this.$once("hook:beforeDestroy", () => {
      delete window._temp;
      delete window._project;
      store.reviewMode = false;
      store.showErrors = false;
      store.application = {};
      store.errors = {};
      window.removeEventListener("turbolinks:before-visit", checkChanges);
    });

    if (
      this.state.project.submitted_at ||
      this.state.isAssessment ||
      this.hasPassed
    ) {
      store.reviewMode = true;
    }

    if (this.competitionForm) {
      const fields = sortBy(this.competitionForm.fieldset, ["order"]);
      this.setApplication(fields);
      this.$once("hook:mounted", () => {
        this.showApplication = true;
      });
    }

    if (this.application && window._temp) {
      if (
        this.application.hasOwnProperty("project_partners") &&
        window._temp.hasOwnProperty("project_partners")
      ) {
        this.application.project_partners = [...window._temp.project_partners];
      }
    }

    this.loadTabWithHash(window.location.hash);
  },
  methods: {
    loadTabWithHash(hash) {
      if (!hash) return;
      hash = hash.replace("#", "");
      const index =
        hash === "grading"
          ? "grading"
          : this.tabs.findIndex(tab => tab.hash === hash);
      if (index < 0) return;
      return this.setActive(index);
    },
    getFieldset(key) {
      const groups = this.competitionForm.fieldset.map(item => {
        const errorField = item.fields.find(field => field.key === key);
        if (errorField)
          return {
            legend: item.legend,
            question: errorField.label
          };
      });
      const group = groups.filter(Boolean);
      return group[0];
    },
    /**
     * hashFriendly: creates a url friendly string of the value
     * @param		{string}	value	the value that will be "Kebabbed"
     * @return	{string}				the kebabbed string e.g. "this-is-kebabbed"
     */
    hashFriendly(value) {
      return kebabCase(value);
    },
    /**
     * setActive: used to set the the active fieldset of the application
     * @param	{int}	index	the index of the fieldset
     */
    setActive(index) {
      window.location.hash = this.tabs[index] ? this.tabs[index].hash : index;
      this.scrollTop();
      this.saveApplication;
      this.active = index;
    },
    /**
     * setApplication: this is used to set the initial values of the
     * application on load. This will then merge the users saved application
     * to the result.
     * @param {object}	application	The application form object
     * @return											Merge the empty application with the users application
     */
    setApplication(application) {
      // set initial defaults

      // Get all the application forms fields
      application.map(({ fields }) =>
        // loop over all the subfields and set the parent key
        fields.map(field => {
          /**
           * check for repeatable field
           * TODO: look at creating seperate methods for each and reduce side-effects
           */
          if (!field.hasOwnProperty("repeatable")) {
            this.$set(this.application, field.key, {});
            return this.setApplicationFieldKeyVal(field);
          } else {
            this.$set(this.application, field.key, []);
            return this.setApplicationFieldArray(field);
          }
        })
      );

      // merge with saved application
      return merge(this.application, window._temp);
    },
    checkDefault(defaultValue) {
      if (typeof defaultValue.match !== "function") return defaultValue;
      if (!defaultValue.match(/\[[A-Z]+\]/g)) return defaultValue;
      return this.matchedValue(defaultValue);
    },
    matchedValue(defaultValue) {
      const value = defaultValue.replace(/[\s\W]+/g, "").toLowerCase();
      const key = Object.freeze({
        company: {
          company_number: this.state.user.company.number,
          company_details: pick(this.state.user.company, [
            "name",
            "address_line_1",
            "address_line_2",
            "town",
            "county",
            "postcode"
          ])
        },
        name: this.state.user.name,
        email: this.state.user.email,
        telephone: this.state.user.telephone || "",
        jobtitle: this.state.user.job_title || ""
      });
      return key[value];
    },
    /**
     * setApplicationFieldKeyVal: if the form section is not multiple/repeatable,
     * then do a straight set.
     * @param	{object}	field	the individual field for the current fieldgroup
     * @return								Side-effect of updating the the user's application with the default attributes defined on the form.
     */
    setApplicationFieldKeyVal(field) {
      return field.sub_fields.map(({ key, attributes }) => {
        return this.$set(
          this.application[field.key],
          key,
          this.checkDefault(attributes.default)
        );
      });
    },
    /**
     * setApplicationFieldKeyArray: Similar to setApplicationFieldKeyVal, the method maps each of the keys to a temp object before setting the minimum amount to the array.
     * @param	{object}	field	the individual field for the current fieldgroup
     * @return								Side-effect of updating the the user's application with the default attributes defined on the form the minimum number of entries required.
     */
    setApplicationFieldArray(field) {
      const temp = {};
      field.sub_fields.map(({ key, attributes }) => {
        return (temp[key] = attributes.default);
      });

      while (this.application[field.key].length < field.repeatable.min) {
        this.application[field.key].push({ ...temp });
      }
    },
    /**
     * saveApplicationButton: Used to handle the save event of the 'Save & Exit' buttons
     * @param	{object}	e	the event data that is passed.
     * @return						side effect of fireing the saveApplication method and firing the click
     */
    saveApplicationButton(e) {
      const returnLink =
        e.target.getAttribute("href") ||
        e.target.parentElement.getAttribute("href");
      window._temp = this.application;
      this.saveApplication(() => Turbolinks.visit(returnLink));
    },
    /**
     * saveApplication: Save the application
     * @param		{function}	cb	a callback function that is fired on a successful request.
     * @return	{function}		the callback that was assigned with the response.
     */
    saveApplication(cb = function() {}) {
      // Dont save on review mode
      if (this.reviewMode) return;
      return axios
        .put(route("applications.process", this.state.project.uuid), {
          application: { ...this.application }
        })
        .then(response => {
          const errors = response.data.errors || {};
          this.errors = errors;
          return cb(response);
        })
        .catch(error => this.requestError(error));
    },
    /**
     * saveOnBlue: used to just fire a quiet save on blur
     * @return	{function}	fires the save application mathod
     */
    saveOnBlur() {
      if (isEqual(this.application, window._temp)) return;
      return this.saveApplication();
    },
    /**
     * checkChanges: make sure that the form has not changed
     * @param		{function}	event	the event object
     * @return	{function}				the save application method
     */
    checkChanges(event) {
      if (this.reviewMode) return;
      if (!isEqual(this.application, window._temp)) {
        if (confirm("Would you like to save you application before leaving?")) {
          event.preventDefault();
          this.saveApplication(() => {
            window._temp = this.application;
            Turbolinks.visit(event.data.url);
          });
        }
      }
    },
    showConfirmation() {
      return this.saveApplication(() => {
        this.confirmationModalVisible = true;
        this.showErrors = true;
      });
    },
    submitApplication() {
      if (this.reviewMode) return;
      window._temp = this.application;
      return axios
        .put(
          route("applications.process", this.state.project.uuid),
          {
            application: { ...this.application }
          },
          {
            headers: {
              "X-APC-Save-Application": true
            }
          }
        )
        .then(() => {
          return Turbolinks.visit("/");
        })
        .catch(error => this.requestError(error));
    }
  }
};
